import _ from "lodash";
import { Part } from "./Part";
import { Empty3d } from "../arrangement3d/Empty3d";
import { v3 } from "oc-threejs/short3";
import * as Maths3 from "oc-threejs/Maths3";
import {
  getElementAnzahlDieInsEmptyPasst,
  getEntferntAngrenzendeFreiraumBreiteSumme,
} from "./elementFunctions";
import * as Maths from "oc-commons/Maths";
import { ZElement } from "./ZElement";
import { Segment } from "./Segment";

export class Empty extends Part {
  static CLASS_NAME = "Empty";

  isEmpty = true;
  declare parent: Segment;

  // ----------------------------------------------------------------------------------
  // persistierte Werte - Zustand, werden mit der Konfiguration gespeichert
  // ----------------------------------------------------------------------------------

  automatischeBreite: boolean | null = null;

  // ----------------------------------------------------------------------------------
  // session Werte - Zustand, werden beim App-Neustart zurückgesetzt
  // ----------------------------------------------------------------------------------

  // ----------------------------------------------------------------------------------
  // abgeleitete Werte - d.h. sind letztlich nur berechnet (in update3d) und gecached, kein Zustand
  // Achtung, diese Werte muss man immer ganz besonders im Auge haben, kann sein,
  // dass sie fälschlich doch als Zustand dienen, und das führt dann zu schwierigen Bugs
  // ----------------------------------------------------------------------------------

  constructor(parent: Segment) {
    super(parent);
  }

  update3d() {
    super.update3d();
    this.snap = this.getSnapFromNachbarn();
  }

  getSnapFromNachbarn() {
    for (let dir of [-1, +1] as const) {
      const nachbar = this.getPartAtRelativeIndex(dir);
      if (nachbar && !(nachbar as Empty).isEmpty && (nachbar as Part).snap != 0) {
        return nachbar.snap || dir;
      }
    }
    return this.snap;
  }

  reset() {
    super.reset();
    this.automatischeBreite = true;
    this.isBreiteKonfigurierbar = true;
  }

  copy(other: Empty) {
    super.copy(other);
    this.automatischeBreite = other.automatischeBreite;
    return this;
  }

  clone() {
    return new Empty(this.parent).copy(this);
  }

  toJSON() {
    const json = super.toJSON();
    json.automatischeBreite = this.automatischeBreite;
    return json;
  }

  fromJSON(json: any) {
    super.fromJSON(json);
    this.automatischeBreite = json?.automatischeBreite;
    return this;
  }

  setKonfigurierbareBreite(breite: number) {
    this.uiRemove();
    const isEmptySegmentÜberstand = () =>
      this.parent.getX(this, 0) + breite > this.parent.breite + 0.1;
    this.breite = breite;
    if (breite > 0.01) {
      this.automatischeBreite = false;
      this.segment.addPart(this);
      const index = this.segment.empties.indexOf(this);
      this.segment.empties.splice(index, 1);
      // Notwendig, weil sonst der Leerraum am Ende des Segments entfernt und vorne wieder eingefügt wird
      if (isEmptySegmentÜberstand()) {
        this.segment.repositionAndCheckSnaps();
      }
    }
  }

  createJaco3d() {
    return new Empty3d(this);
  }

  getBox() {
    const highestNachbar = this.getHöchstesAnliegendesElement();
    return Maths3.boxFromGridMinAndSize(
      v3(0, 0, 0.5),
      v3(this.breite, highestNachbar?.höhe ?? 1.8, 0.1)
    );
  }

  getHöchstesAnliegendesElement() {
    return _.maxBy([+1, -1] as const, (dir) => this.getPartAtRelativeIndex(dir)) as any;
  }

  uiRemove(aufruecken = true) {
    aufruecken && this.elementeAufruecken(false);
    this.segment.remove(this);
  }

  elementeAufruecken(beidseitig: boolean) {
    this.segment.setVerteilungsmodusIndividuell();
    // todo gemeinsamer Code mit ifCollisionsResnap
    const findDurchgehendAnliegendeElementreihe = (snapDirection: number) => {
      const parts = this.segment.getParts();
      let index = parts.indexOf(this);
      return this.segment.findDurchgehendAnliegendeElementreihe(parts, snapDirection, index, false);
    };
    const snapDaes = [+1, -1] as const;
    const elementreihenNachRichtung = snapDaes.map(
      (snapDirection) =>
        [snapDirection, findDurchgehendAnliegendeElementreihe(snapDirection)] as const
    );

    const elementreihen = elementreihenNachRichtung.filter(
      ([, dae]) => beidseitig || dae.length > 0
    );

    if (!beidseitig && elementreihen.length === 2) {
      const countSnapsLinks = this.segment.parts.filter((p) => p.snap < 0).length;
      const countSnapsRechts = this.segment.parts.filter((p) => p.snap > 0).length;
      const snapDirection = countSnapsLinks > countSnapsRechts ? -1 : +1;
      elementreihen.forEach(([, dae]) => dae.forEach((x) => (x.snap = snapDirection)));
      return;
    }

    elementreihen.forEach(([snapDirection, dae]) => {
      dae.forEach((x) => (x.snap = -snapDirection));
    });
  }

  fillWithElements(
    element: ZElement,
    {
      allowMassElement = false,
      minimalOneElement = false,
      allowToGrowToMaximum = false,
      maximumNumberOfElements = Number.POSITIVE_INFINITY,
    }: {
      allowMassElement?: boolean;
      minimalOneElement?: boolean;
      allowToGrowToMaximum?: boolean;
      maximumNumberOfElements?: number;
    } = {}
  ) {
    return this.arrangement.forNurMasseUpdaten(() => {
      const epsilon = 0.001;
      this.automatischeBreite = true;
      const segment = this.segment;
      const arrangement = this.arrangement;
      const snap = this.snap;
      const nachbarElemente = this.getSegmentNachbarn();
      let elementBreite = element.breite;

      if (elementBreite < epsilon) {
        console.error("part.fillWithElements with an element of breite=0", element);
        return [];
      }

      let added: Part[] = [];

      if (allowToGrowToMaximum) {
        const angrenzendeFreiraumBreite = getEntferntAngrenzendeFreiraumBreiteSumme(this);
        if (
          angrenzendeFreiraumBreite > 0.001 &&
          elementBreite > this.breite &&
          elementBreite < this.breite + angrenzendeFreiraumBreite
        ) {
          this.automatischeBreite = false;
          this.breite += angrenzendeFreiraumBreite;
          segment.repositionAndCheckSnaps();
        }
      }

      const multipleElementsAllowed = !(element.isTor || element.isAnschluss);

      const masselementFitsPerfectly =
        allowMassElement &&
        elementBreite < element.sizeRange.box.max.x + epsilon &&
        this.breite < element.sizeRange.box.max.x + epsilon &&
        elementBreite < this.breite + epsilon;

      if (masselementFitsPerfectly) {
        elementBreite = element.sizeRange.box.max.x;
        element.setBreite(elementBreite);
        // debugger;
      }

      // in der anzahl steckt die Restbreite drin (anzahl%1 * element.breite)
      let {
        anzahlGanzeElemente,
        restBreite,
        gesamtBreiteElemente,
        sollBreite,
        sollBreiteElemente,
        deltaLinks,
        deltaRechts,
        elementZwischenAbstand,
      } = getElementAnzahlDieInsEmptyPasst(element, this);
      if (!masselementFitsPerfectly && !multipleElementsAllowed) {
        anzahlGanzeElemente = Math.min(1, anzahlGanzeElemente);
      }
      if (!allowMassElement && minimalOneElement) {
        anzahlGanzeElemente = Math.max(1, anzahlGanzeElemente);
      }
      anzahlGanzeElemente = Math.min(maximumNumberOfElements, anzahlGanzeElemente);

      const xStart = segment.getX(this, snap == -1 ? 0 : 1);
      let { added: addedStandardElemente, currentX } = segment.addElemente(
        xStart,
        element,
        elementZwischenAbstand,
        anzahlGanzeElemente,
        snap
      );
      added = added.concat(addedStandardElemente);

      // console.log(
      //   "Standard-Elemente added",
      //   Debugs.roundValues({
      //     sollBreite,
      //     sollBreiteElemente,
      //     elementBreite,
      //     anzahlGanzeElemente,
      //     gesamtBreiteElemente,
      //     restBreite,
      //     addedLength: added.length,
      //     elementZwischenAbstand,
      //     xStart,
      //     currentX,
      //     deltaLinks,
      //     deltaRechts,
      //     snap,
      //   })
      // );

      if (
        restBreite > epsilon &&
        allowMassElement &&
        (multipleElementsAllowed || anzahlGanzeElemente == 0) &&
        added.length < maximumNumberOfElements
      ) {
        // weil mindestens ein weiteres Element hinzugefügt werden wird
        if (anzahlGanzeElemente > 0) {
          restBreite -= elementZwischenAbstand;
        }

        const elementMinBreite = Math.max(
          arrangement.getElementMinBreite(),
          element.sizeRange.box.min.x
        );
        const clamp = (breite: number) =>
          Maths.cutBetween(elementMinBreite, breite, element.sizeRange.box.max.x);
        const massElementBedingung = () => restBreite + epsilon > elementMinBreite;
        if (
          (restBreite < elementMinBreite || restBreite > element.sizeRange.box.max.x) &&
          added.length > 0
        ) {
          // Falls die letzte Lücke nicht geschlossen werden kann, verteile den Leerraum auf zwei
          // möglichst gleichbreite Maßelemente. Mache dazu das letzte Standardelement zu einem Maßelement
          // und füge ein weitere hinzu
          const lastElement = _.last(added);
          const neueRestBreite = restBreite + lastElement.breite;
          const neueBreite1 = clamp(neueRestBreite / 2);
          if (Math.abs(neueBreite1 - lastElement.breite) > epsilon) {
            currentX += snap * (lastElement.breite - neueBreite1);
            lastElement.setBreite(neueBreite1);
            restBreite = neueRestBreite - neueBreite1;
            // console.log(
            //   "nicht genug Platz für ein einzelnes Maßelement, mache das letzte Element zu einem schmalen:",
            //   Debugs.roundValues({
            //     lastElementBreite: lastElement.breite,
            //     currentX,
            //     restBreite,
            //     neueRestBreite,
            //   })
            // );
          }
        }
        if (massElementBedingung() || (minimalOneElement && added.length == 0)) {
          const clone = element.clone();
          clone.snap = snap;
          clone.parent = segment;
          clone.setBreite(clamp(restBreite));
          segment.setX(clone, snap == -1 ? 0 : 1, currentX);
          // console.log(
          //   "füge ein Maßelement ein:",
          //   Debugs.roundValues({
          //     cloneBreite: clone.breite,
          //     verbleibenderRest: restBreite - clone.breite,
          //     currentX,
          //   })
          // );
          segment.addPart(clone);
          added.push(clone);
        }
      }

      if (added.length > 0) {
        // Es kann sein, dass einer der Nachbarn snap 0 hatte und die neu hinzugefügten Elemente
        // davon wegsnappen. Dadurch würden Fehler entstehen, z.B. kleine Lücken nicht aufgefüllt.
        // Also muss dieser Nachbar auf den eigenen Snap gesetzt werden
        const nachbar = nachbarElemente[snap == -1 ? 1 : 0];
        if ((nachbar as ZElement)?.isElement && nachbar?.snap === 0) {
          nachbar.snap = snap;
        }

        // und final updaten
        arrangement.update3d();
        // this.segment.logit();
        APP.dirty();
      }

      return added;
    });
  }
}
